/**
 * PHARMAKON — BETA in-memory store
 *
 * Singleton holding every entity used by the demo backend. Each entity is a
 * plain array (or object for singletons like the pharmacie) that handlers
 * mutate directly. The whole store is persisted to localStorage after each
 * mutation so a page refresh keeps the user's demo session intact.
 *
 * Reset semantics: `resetStore()` wipes the store + the localStorage copy
 * (called on logout). `seedStore(role)` rebuilds it from the seed modules
 * for the requested demo role.
 */
import { buildSeed } from "./seed.js";
import { resetIdCounter } from "./uuid.js";

const STORE_KEY = "pharmakon_beta_store";

/** Single live reference handlers mutate. */
export const store = {
  /** Empty until `seedStore()` is called. */
  ready: false,
  role: null,
  /** Filled in by buildSeed() — see ./seed.js for the full shape. */
  data: {},
};

/** Persist current store to localStorage. */
function persist() {
  try {
    localStorage.setItem(
      STORE_KEY,
      JSON.stringify({ role: store.role, data: store.data }),
    );
  } catch {
    /* quota / private mode — accept loss of persistence */
  }
}

/** Try to restore a previously-persisted store. Returns true on success. */
function restore() {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    if (!parsed?.data) return false;
    store.role = parsed.role || null;
    store.data = parsed.data;
    store.ready = true;
    return true;
  } catch {
    return false;
  }
}

/**
 * Build (or rebuild) the demo dataset for `role`. Always called when
 * entering BETA from the login screen — overwrites any prior session.
 */
export function seedStore(role) {
  resetIdCounter();
  store.role = role;
  store.data = buildSeed(role);
  store.ready = true;
  persist();
}

/** Lazy-load: if the store wasn't seeded yet (after a refresh), restore. */
export function ensureStore() {
  if (store.ready) return store;
  if (restore()) return store;
  // Last-resort fallback: build a PROPRIETAIRE dataset so handlers don't
  // crash if the flag is set but no seed exists yet.
  seedStore("PROPRIETAIRE");
  return store;
}

/** Mutate-and-persist helper used by handlers. */
export function commit() {
  persist();
}

/** Full wipe — called on logout. */
export function resetStore() {
  store.ready = false;
  store.role = null;
  store.data = {};
  try {
    localStorage.removeItem(STORE_KEY);
  } catch {
    /* ignore */
  }
  resetIdCounter();
}
